import React, {JSX, useEffect, useMemo, useState} from 'react';
import {HibuTag, HibuTags, Span} from '../../../../search-types';
import {useTranslation} from 'react-i18next';
import {EditableTag} from '../../../../../common/editable-tag/editableTag';
import {SvgIcon} from '../../../../../icons/svgIcon';
import {Icons} from '../../../../../icons/icons';
import {buildDefaultFieldTitle, isEmptyArray} from '../../../../../utils/utils';
import classNames from 'clsx';
import {useLocalizationUtil} from '../../../../../localizationUtil';
import createOption from '../../../../../common/select/hibuSelectUtil';
import {Option} from '../../../../../common/select/select';
import {
    useDetailFields,
    useSettingsTogglesConfig,
    useSpotlightTagTypes,
    useTagDropdownOptions,
    useTagsWithConfirmation,
} from '../../../../../config/config-hooks';
import {useStateManualValidation} from '../../../../../settings/duck/settings-hooks';
import {EditableTagWithOptions} from '../../../../../common/editable-tag/editableTagWithOptions';
import {getFieldNamesFromFacets} from '../details.utils';
import {HibuFacetGroup} from '../../../../../facets/facet.types';
import {useFacets} from '../../../../../facets/duck/filter.hooks';
import {buildDropdownTagUpdate} from './hibuTagList.utils';

type HibuTagListProps = {
    filteredDocumentTags: HibuTags;
    removeTag: (tagCategory: string, tag: HibuTag) => void;
    changeTag: (
        tagCategory: string,
        tag: HibuTag,
        labels: { [language: string]: string } | null,
        extra: { [key: string]: any } | null,
        spans?: Span[],
        updatedTagId?: string | null,
    ) => void;
    addTag: (
        tagCategory: string,
        tagId: string | null,
        labels: { [language: string]: string } | null,
        extra: { [key: string]: any } | null,
    ) => void;
};

export const HibuTagList = React.memo((props: HibuTagListProps) => {
    const {t} = useTranslation();
    const {formatNumber, selectedLanguage: currentLanguage} = useLocalizationUtil();

    const tagGroupsWithUserValidation = new Set(useTagsWithConfirmation().map((tag) => tag.split('.').pop()));
    const tagDropdownOptions = useTagDropdownOptions();
    const configuredDetailFields = useDetailFields();
    const facets: HibuFacetGroup[] = useFacets();
    const spotlightTagTypes = useSpotlightTagTypes();
    const spotlightTagTypeNames = useMemo(() => {
        return new Set(spotlightTagTypes.map(t => t.name));
    }, [spotlightTagTypes]);

    const showManualValidation = useStateManualValidation();
    const showManualValidationSetting = useSettingsTogglesConfig().showManualValidation;
    const isEditAllowed = showManualValidationSetting && showManualValidation;

    const [showEmptyCategories, setShowEmptyCategories] = useState<boolean>(false);
    const [currentCategoryUnderModification, setCurrentCategoryUnderModification] = useState<string | null>(null);
    const [emptyCategories, setEmptyCategories] = useState<string[]>([]);

    // Track disabled state for each tag
    const [buttonDisabledMap, setButtonDisabledMap] = useState(new Map<string, boolean>());

    useEffect(() => {
        setButtonDisabledMap(new Map<string, boolean>());

        const tagTypesFromFacets: string[] = getFieldNamesFromFacets(facets, 'tags');
        const next = extractEmptyCategories(
            isEmptyArray(configuredDetailFields?.detailTagTypes) ? tagTypesFromFacets : (configuredDetailFields?.detailTagTypes ?? [])
        );
        setEmptyCategories(prev => shallowEqualArray(prev, next) ? prev : next);

        function extractEmptyCategories(tagTypes: string[]) {
            return tagTypes
                .filter((name) => !spotlightTagTypeNames.has(name))
                .filter((name) => !props.filteredDocumentTags[name] || props.filteredDocumentTags[name].length <= 0);
        }
    }, [configuredDetailFields?.detailTagTypes, facets, props.filteredDocumentTags, spotlightTagTypeNames]);

    function shallowEqualArray(a: string[] = [], b: string[] = []) {
        if (a.length !== b.length) return false;
        for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
        return true;
    }

    if (!props?.filteredDocumentTags) {
        return null;
    }

    function hasOptions(tagGroupName: string): boolean {
        return tagDropdownOptions?.[tagGroupName] !== undefined;
    }

    const onSave = (value: string | Option, tagGroupName: string) => {
        // Distinguish between value from normal input and value from Select field
        const id: string = typeof value === 'string' ? null : value.value;
        const label: string = typeof value === 'string' ? value : value.label;
        const extra: { [key: string]: any } = {
            userValidated_bool: true,
        };
        props.addTag(tagGroupName, id, {[currentLanguage]: label}, extra);
        setCurrentCategoryUnderModification(null);
    };

    const onChangeLabel = (modifiedLabel: string, tag: HibuTag, tagGroupName: string) => {
        // Immediately disable the confirmation button for this tag
        setButtonDisabledMap(prev => ({...prev, [tag.id]: true}));
        const extra: { [key: string]: any } = {
            ...tag.extra,
            userValidated_bool: true,
        };
        props.changeTag(tagGroupName, tag, {[currentLanguage]: modifiedLabel}, extra, tag.spans);
    };

    const onChangeOption = (option: Option, tag: HibuTag, tagGroupName: string) => {
        console.log('onChange');
        // Immediately disable the confirmation button for this tag
        setButtonDisabledMap((prev) => ({...prev, [tag.id]: true}));
        const updatedTag = buildDropdownTagUpdate(tag, option, currentLanguage);
        props.changeTag(tagGroupName, tag, updatedTag.labels, updatedTag.extra, tag.spans, updatedTag.tagId);
    };

    const onDeleteTag = (tagGroupName: string, tag: HibuTag) => {
        props.removeTag(tagGroupName, tag);
    };

    const onChangeUserValidation = (tag: HibuTag, tagGroupName: string) => {
        const extra: { [key: string]: any } = {
            ...tag.extra,
            userValidated_bool: !tag.extra?.userValidated_bool,
        };
        props.changeTag(tagGroupName, tag, null, extra);
    };

    const isNotAggregated = (tagGroupElement: HibuTag[]): boolean => {
        return !tagGroupElement.some(tag => tag.extra?.aggregated);
    };

    const shouldRenderTagGroup = (tagGroupName: string, tagGroupValues: HibuTag[]): boolean => {
        return (
            tagGroupValues &&
            tagGroupValues.length > 0 &&
            isNotAggregated(tagGroupValues) &&
            tagGroupName !== 'heading' &&
            tagGroupName !== 'sectionType'
        );
    };

    const renderAddNewTagButton = (category: string) => (
        <div className="add-tag-placeholder" onClick={() => setCurrentCategoryUnderModification(category)}>
            <SvgIcon className="add-tag" icon={Icons.ADD} alt={t('search.details.add')}/>
            <span>{t('search.details.add')}</span>
        </div>
    );

    const renderNewInput = (tagGroupName: string) => {
        const userValidationEnabled = tagGroupsWithUserValidation.has(tagGroupName);
        return (
            <>
                {hasOptions(tagGroupName) ? (
                    <EditableTagWithOptions
                        value={undefined}
                        onSubmit={(value) => onSave(value, tagGroupName)}
                        onUserValidation={() => setCurrentCategoryUnderModification(null)}
                        onDelete={() => setCurrentCategoryUnderModification(null)}
                        allowEditing={isEditAllowed}
                        tagGroupName={tagGroupName}
                        userValidationEnabled={userValidationEnabled}
                    />
                ) : (
                    <EditableTag
                        fieldType="line"
                        value={''}
                        onSubmit={(value) => onSave(value, tagGroupName)}
                        onUserValidation={() => setCurrentCategoryUnderModification(null)}
                        onDelete={() => setCurrentCategoryUnderModification(null)}
                        onCancel={() => setCurrentCategoryUnderModification(null)}
                        allowEditing={isEditAllowed}
                        userValidationEnabled={userValidationEnabled}
                    />
                )}
            </>
        );
    };

    const renderTagGroupValues = (tagGroupName: string, tagGroupValues: HibuTag[]) => {
        const userValidationEnabled = tagGroupsWithUserValidation.has(tagGroupName);
        return (
            <div className="tag-value cell">
                {tagGroupValues?.map((tag: HibuTag) => {
                    const userValidated = tag.extra?.['userValidated_bool'];
                    return isEditAllowed && hasOptions(tagGroupName) ? (
                        <EditableTagWithOptions
                            value={createOption(tag.id, tag.label)}
                            onSubmit={(option: Option) => onChangeOption(option, tag, tagGroupName)}
                            onUserValidation={() => onChangeUserValidation(tag, tagGroupName)}
                            onDelete={() => onDeleteTag(tagGroupName, tag)}
                            allowEditing={isEditAllowed}
                            tagGroupName={tagGroupName}
                            userValidationEnabled={userValidationEnabled}
                            userValidated={buttonDisabledMap[tag.id] || userValidated}
                        />
                    ) : (
                        <EditableTag
                            fieldType="line"
                            value={tag.label}
                            onSubmit={(value) => onChangeLabel(value, tag, tagGroupName)}
                            onUserValidation={() => onChangeUserValidation(tag, tagGroupName)}
                            onDelete={() => onDeleteTag(tagGroupName, tag)}
                            onCancel={() => setCurrentCategoryUnderModification(null)}
                            allowEditing={isEditAllowed}
                            userValidationEnabled={userValidationEnabled}
                            userValidated={buttonDisabledMap[tag.id] || userValidated}
                            tagGroupName={tagGroupName}
                            tag={tag}
                        />
                    );
                })}
            </div>
        );
    };

    const renderTagGroupName = (tagGroupName: string) => {
        const title = t('fieldTitle.tagsPlural.' + tagGroupName + '.label', buildDefaultFieldTitle(tagGroupName));
        return <div className="tag-name cell">{title}</div>;
    };

    const renderAddTagElement = (category: string) => {
        return (
            isEditAllowed && (
                <div className="add-tag-element tag-value cell">
                    {currentCategoryUnderModification === category
                        ? renderNewInput(category)
                        : renderAddNewTagButton(category)}
                </div>
            )
        );
    };

    const renderTagGroup = (tagGroupName: string, tagGroupValues: HibuTag[], tagIndex: number) => {
        if (shouldRenderTagGroup(tagGroupName, tagGroupValues)) {
            return (
                <div key={tagIndex} className="document-tag">
                    {/*1.*/}
                    {renderTagGroupName(tagGroupName)}
                    {/*2.*/}
                    {renderTagGroupValues(tagGroupName, tagGroupValues)}
                    {/*3.*/}
                    {renderAddTagElement(tagGroupName)}
                </div>
            );
        }
    };

    const renderTagGroupForEmptyCategories = (tagGroupName: string, tagIndex: number) => {
        return (
            <div key={tagIndex} className="document-tag">
                {/*1.*/}
                {renderTagGroupName(tagGroupName)}
                {/*2.*/}
                {renderAddTagElement(tagGroupName)}
            </div>
        );
    };

    const renderTags = (): (JSX.Element | undefined)[] => {
        return isEmptyArray(configuredDetailFields?.detailTagTypes) ? renderAllTagTypes : renderConfiguredTagTypes;
    };

    const renderConfiguredTagTypes = configuredDetailFields?.detailTagTypes?.map((tagTypeName: string, tagIndex) => {
        const tagGroupValues = props.filteredDocumentTags[tagTypeName];
        return renderTagGroup(tagTypeName, tagGroupValues, tagIndex);
    });

    const renderAllTagTypes = Object.entries(props.filteredDocumentTags).map((tagGroup, tagIndex) => {
        const tagTypeName = tagGroup[0];
        const tagGroupValues = tagGroup[1];
        return renderTagGroup(tagTypeName, tagGroupValues, tagIndex);
    });

    const renderEmptyCategories = (): JSX.Element[] | null => {
        if (!showEmptyCategories) {
            return null;
        }
        return emptyCategories
            ?.map((category: string, index: number) => {
                return renderTagGroupForEmptyCategories(category, index);
            });
    };

    const renderEmptyCategoriesElement = () => {
        const emptyCategoriesCount = emptyCategories.length;
        return emptyCategories?.length >= 1 &&
            (
                <div className="empty-categories-element">
                    <div
                        className="empty-categories-placeholder"
                        onClick={() => setShowEmptyCategories(!showEmptyCategories)}
                    >
                        {showEmptyCategories && t('search.details.closeCategories')}
                        {!showEmptyCategories &&
                            (emptyCategoriesCount > 1
                                ? t('search.details.emptyCategories', {entry: formatNumber(emptyCategoriesCount)})
                                : t('search.details.emptyCategory', {entry: formatNumber(emptyCategoriesCount)}))}
                    </div>
                </div>
            );
    };

    return (
        <div className={classNames('document-infos', 'hibu-tags')}>
            {renderTags()}
            {renderEmptyCategoriesElement()}
            {renderEmptyCategories()}
        </div>
    );
});
